import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Drag-to-reveal before/after slider.
 * Props:
 *  - beforeImage / afterImage: URLs
 *  - altBefore / altAfter: alt text
 *  - initialPosition: 0–100, defaults to 50
 */
export default function ImageCompareSlider({
  beforeImage,
  afterImage,
  altBefore = "Before",
  altAfter = "After",
  initialPosition = 50,
  className = "",
}) {
  const [pos, setPos] = useState(initialPosition);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  const moveTo = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    setPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => moveTo(e.clientX);
    const onTouch = (e) => moveTo(e.touches[0].clientX);
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("touchmove", onTouch);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchend", onUp);
    document.body.style.cursor = "ew-resize";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("touchmove", onTouch);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchend", onUp);
      document.body.style.cursor = "";
    };
  }, [dragging]);

  return (
    <div
      ref={containerRef}
      onMouseDown={() => setDragging(true)}
      onTouchStart={() => setDragging(true)}
      className={`relative w-full h-full overflow-hidden select-none group cursor-ew-resize ${className}`}
    >
      {/* After (bottom layer) */}
      <img
        src={afterImage}
        alt={altAfter}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* Before (top layer, clipped) */}
      <div
        className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
        style={{
          clipPath: `polygon(0 0, ${pos}% 0, ${pos}% 100%, 0 100%)`,
        }}
      >
        <img
          src={beforeImage}
          alt={altBefore}
          draggable={false}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Labels */}
      <div
        className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-charcoal bg-cream/90 backdrop-blur-sm px-3 py-1.5 transition-opacity"
        style={{ opacity: pos > 12 ? 1 : 0 }}
      >
        Before
      </div>
      <div
        className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase text-charcoal bg-brass/95 px-3 py-1.5 transition-opacity"
        style={{ opacity: pos < 88 ? 1 : 0 }}
      >
        After
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 h-full w-px"
        style={{ left: `calc(${pos}% - 0.5px)` }}
      >
        <div className="absolute inset-y-0 w-px bg-cream/80 shadow-[0_0_18px_rgba(253,251,247,0.45)]" />
        <div
          role="slider"
          aria-valuenow={Math.round(pos)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Drag to compare before and after"
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-cream text-charcoal shadow-2xl flex items-center justify-center transition-transform duration-300 ${
            dragging ? "scale-110" : "group-hover:scale-105"
          }`}
        >
          <div className="flex items-center text-brassDark">
            <ChevronLeft className="h-5 w-5" />
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>
      </div>
    </div>
  );
}
